/**
 * The flow editor's model — what the concept kept in `flow/model.js`, minus
 * everything that was a simulation.
 *
 * ## WHAT MOVED TO THE SERVER, AND WHY
 *
 * The concept computed its pre-flight, its run-mode volumes, its test walk and
 * its suggestions in the browser, over sample data, because it had no server.
 * Here all four are API calls (`/preflight`, `/stats`, `/test`, `/suggest`)
 * and nothing in this file guesses a figure: a run-mode number the browser
 * invented would be a second source of truth beside `flow_steps`, and the
 * first time they disagreed the reader would have no way to know which was
 * real. What stays here is pure and structural — labels, defaults for a new
 * step, edge bookkeeping when a step's ports change, IST date handling, and
 * turning a contract's refusal into a sentence about a step.
 *
 * ## THE GRAPH IS THE SHARED CONTRACT'S
 *
 * Nodes and edges are `FlowNode` / `FlowEdge` from `@wend/shared`, unchanged —
 * no editor-only fields ride along on a node, so what autosave sends is what
 * the editor drew. Ports come from the shared {@link portsOf}, so the "+" on a
 * WhatsApp button and the engine's routing of a tap on it can never disagree
 * about the button's name.
 */
import {
  FIXED_FIELDS,
  FLOW_HOLDOUT_PORT,
  FLOW_JOURNEY_FIELD_LABEL,
  FLOW_NODE_TYPE_LABEL,
  FLOW_TIMEZONE,
  OPERATORS_BY_TYPE,
  WEEKDAY_NAMES,
  edgeFrom,
  portsOf,
  reachableFrom,
  ruleLeafSummary,
  type Field,
  type FieldType,
  type FlowConditionData,
  type FlowEdge,
  type FlowGraph,
  type FlowJourneyField,
  type FlowJourneyRule,
  type FlowNode,
  type FlowNodeOf,
  type FlowNodeType,
  type FlowTestStep,
  type FlowUpdateData,
  type FlowWaitData,
  type OutcomeType,
  type RuleFieldInfo,
  type RuleLeaf,
  type RuleOperator,
  type SegmentSummary,
  type WaVariableBinding,
} from '@wend/shared';
import type { ZodError } from 'zod';
import { formatRupees } from '../../lib/format';

/* -------------------------------------------------------------------------- */
/* What the editor knows besides the graph                                     */
/* -------------------------------------------------------------------------- */

/** An approved template a message step may use. Pending and rejected ones never reach the editor. */
export interface FlowTemplateOption {
  id: string;
  name: string;
  channel: 'whatsapp' | 'email';
  subject: string | null;
}

/** The parts of a WhatsApp template the canvas draws: the bubble and its quick replies. */
export interface WaTemplateShape {
  header: string | null;
  body: string;
  footer: string | null;
  /** Quick-reply labels only, in order — each one is a port. URL buttons do not branch. */
  buttons: string[];
  /** Variable positions the template needs a value for. */
  positions: number[];
}

export interface FlowEditorData {
  segments: readonly SegmentSummary[];
  templates: readonly FlowTemplateOption[];
  /** Filled lazily, per template, as message steps need them. */
  waShapes: ReadonlyMap<string, WaTemplateShape>;
  /** Null when the catalogue could not be read — distinct from a workspace with no columns. */
  fields: readonly Field[] | null;
  outcomeTypes: readonly OutcomeType[];
}

export const EMPTY_EDITOR_DATA: FlowEditorData = {
  segments: [],
  templates: [],
  waShapes: new Map(),
  fields: null,
  outcomeTypes: [],
};

/* -------------------------------------------------------------------------- */
/* Numbers and time                                                            */
/* -------------------------------------------------------------------------- */

export const n = (value: number): string => Math.round(value).toLocaleString('en-IN');
/**
 * The app's own rupee formatter (`lib/format.ts`): whole rupees bare, anything
 * else to two places. This used to drop the trailing zero (`₹2,041.2`) and so
 * disagreed with every other money figure in the app (slice 71 review).
 */
export const rupees = (paise: number): string => formatRupees(paise);

const IST_OFFSET_MIN = 330;
const pad = (value: number) => String(value).padStart(2, '0');

/** `2026-10-20T10:00`, read as IST → an ISO instant. Null for anything that is not that shape. */
export function istLocalToIso(local: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match.map(Number) as [number, number, number, number, number, number];
  const ms = Date.UTC(y, m - 1, d, hh, mm) - IST_OFFSET_MIN * 60_000;
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** An ISO instant → `2026-10-20T10:00` in IST, for a `datetime-local` input. */
export function isoToIstLocal(iso: string): string {
  const t = new Date(Date.parse(iso) + IST_OFFSET_MIN * 60_000);
  if (Number.isNaN(t.getTime())) return '';
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}T${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`;
}

/** Today's date in IST, `YYYY-MM-DD`. */
export function todayIst(now: Date = new Date()): string {
  return isoToIstLocal(now.toISOString()).slice(0, 10);
}

/** Tomorrow at a given IST hour, as an ISO instant — the default for a one-off schedule or an until-wait. */
export function tomorrowAtIst(hour: number, now: Date = new Date()): string {
  const today = todayIst(now);
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1, hour, 0) - IST_OFFSET_MIN * 60_000).toISOString();
}

/** "Tue 20 Oct, 10:00" — always IST, because flow time is IST. */
export function fmtIst(iso: string, withTime = true): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    timeZone: FLOW_TIMEZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  });
}

/** "Saved 12s ago" — the top bar's clock. */
export function savedAgo(savedAt: number | null, now: number): string {
  if (savedAt === null) return 'Not saved yet';
  const s = Math.max(0, Math.round((now - savedAt) / 1000));
  if (s < 5) return 'Saved just now';
  if (s < 60) return `Saved ${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `Saved ${m} min ago`;
  return `Saved ${Math.round(m / 60)}h ago`;
}

/* -------------------------------------------------------------------------- */
/* Labels                                                                      */
/* -------------------------------------------------------------------------- */

export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function waitLabel(data: FlowWaitData): { pre: string; main: string } {
  switch (data.mode) {
    case 'duration': {
      const unit = data.unit === 'minutes' ? 'minute' : data.unit === 'hours' ? 'hour' : 'day';
      return { pre: 'For', main: `${data.amount} ${unit}${data.amount === 1 ? '' : 's'}` };
    }
    case 'until':
      return { pre: 'Until', main: fmtIst(data.at) };
    case 'next':
      return { pre: 'Until the next', main: `${data.weekday === null ? '' : `${WEEKDAY_NAMES[data.weekday]} `}${data.time} IST`.trim() };
  }
}

export function waitText(data: FlowWaitData): string {
  const { pre, main } = waitLabel(data);
  return pre === 'For' ? main : `${pre} ${main}`;
}

export function updateLabel(data: FlowUpdateData): string {
  switch (data.action) {
    case 'add_tag':
      return `Tag: ${data.value}`;
    case 'remove_tag':
      return `Remove tag: ${data.value}`;
    case 'set_field':
      return `Set ${data.key} = ${data.value === '' ? '(empty)' : data.value}`;
  }
}

const JOURNEY_TEXT: Readonly<Record<FlowJourneyField, readonly [string, string]>> = {
  replied_since_entry: ['Replied since entering', 'No reply since entering'],
  clicked_since_entry: ['Clicked since entering', 'No click since entering'],
  opened_since_entry: ['Opened an email since entering', 'No email opened since entering'],
  converted_since_entry: ['Converted since entering', 'Not converted since entering'],
};

export function journeyRuleText(rule: FlowJourneyRule): string {
  const [yes, no] = JOURNEY_TEXT[rule.field];
  return rule.op === 'is_true' ? yes : no;
}

export function isJourneyRule(rule: FlowConditionData['rules'][number]): rule is FlowJourneyRule {
  return rule.kind === 'journey';
}

/** How the catalogue describes a field to the rule helpers; null for a key it does not hold. */
export function fieldInfo(fields: readonly Field[] | null, key: string): RuleFieldInfo | null {
  const field = fields?.find((f) => f.key === key);
  if (field) return { type: field.type, label: field.label, needsDateFormat: field.needsDateFormat };
  const fixed = (FIXED_FIELDS as Readonly<Record<string, { label: string; type: FieldType }>>)[key];
  return fixed ? { type: fixed.type, label: fixed.label } : null;
}

/** "Replied since entering and Age > 25?" — the condition's question, as the canvas prints it. */
export function conditionLabel(data: FlowConditionData, fields: readonly Field[] | null): string {
  const parts = data.rules.map((rule) =>
    isJourneyRule(rule) ? journeyRuleText(rule) : ruleLeafSummary(rule, fieldInfo(fields, rule.field)),
  );
  return `${parts.join(data.join === 'or' ? ' or ' : ' and ')}?`;
}

export function goalLabel(outcomeTypeId: string, outcomeTypes: readonly OutcomeType[]): string {
  if (outcomeTypeId === 'any') return 'Any conversion';
  return outcomeTypes.find((type) => type.id === outcomeTypeId)?.name ?? 'An outcome type that no longer exists';
}

/** The step's name as a heading or an aria-label says it. */
export function nodeName(node: FlowNode): string {
  if (node.type === 'message') return node.data.channel === 'email' ? 'Email' : 'WhatsApp message';
  return FLOW_NODE_TYPE_LABEL[node.type];
}

export function splitSum(node: FlowNodeOf<'split'>): number {
  return node.data.rows.reduce((sum, row) => sum + row.pct, 0) + (node.data.holdout?.pct ?? 0);
}

/* -------------------------------------------------------------------------- */
/* Ids                                                                         */
/* -------------------------------------------------------------------------- */

/** A fresh id that is not in `taken` — short, and inside the contract's `[A-Za-z0-9_-]{1,64}`. */
export function freshId(prefix: string, taken: ReadonlySet<string>): string {
  for (;;) {
    const id = `${prefix}${Math.random().toString(36).slice(2, 8)}`;
    if (!taken.has(id)) return id;
  }
}

export function takenIds(graph: FlowGraph): Set<string> {
  return new Set([...graph.nodes.map((node) => node.id), ...graph.edges.map((edge) => edge.id)]);
}

/* -------------------------------------------------------------------------- */
/* New steps                                                                   */
/* -------------------------------------------------------------------------- */

export type NodeSpec = { [T in FlowNodeType]: { type: T; data: FlowNodeOf<T>['data']; suggested?: { reason: string } } }[FlowNodeType];

/** Bindings for a template's positions: the person's name, with a fallback, so no greeting is empty. */
export function defaultBindings(positions: readonly number[], existing: readonly WaVariableBinding[] = []): WaVariableBinding[] {
  return positions.map(
    (position) => existing.find((binding) => binding.position === position) ?? { position, source: 'name', fallback: 'there' },
  );
}

export function whatsappSpec(templateId: string | null, shape: WaTemplateShape | undefined): NodeSpec {
  return {
    type: 'message',
    data: {
      channel: 'whatsapp',
      templateId,
      buttons: shape?.buttons ?? [],
      noReplyHours: 24,
      vars: defaultBindings(shape?.positions ?? []),
    },
  };
}

export function emailSpec(templateId: string | null): NodeSpec {
  return { type: 'message', data: { channel: 'email', templateId, fromEmail: null, fromName: null } };
}

export const LOGIC_TEMPLATES = [
  { id: 'wait-check', label: 'Wait 1 day, then check for a reply', hint: 'Wait → Condition' },
  { id: 'ab', label: 'A/B test · 50 / 50 with a holdout', hint: 'Random split, two paths and 10% held out' },
  { id: 'quiet', label: 'Hold until 10:00 IST', hint: 'Wait until the next 10:00' },
] as const;
export type LogicTemplateId = (typeof LOGIC_TEMPLATES)[number]['id'];

export function specsFor(kind: 'condition' | 'split' | 'wait' | 'update' | 'goal' | 'exit' | 'comment', idFor: (prefix: string) => string): NodeSpec[] {
  switch (kind) {
    case 'condition':
      return [{ type: 'condition', data: { join: 'and', rules: [{ kind: 'journey', id: idFor('r'), field: 'replied_since_entry', op: 'is_true' }] } }];
    case 'split':
      return [{ type: 'split', data: { rows: [{ id: 'a', label: 'A', pct: 50 }, { id: 'b', label: 'B', pct: 50 }], holdout: null } }];
    case 'wait':
      return [{ type: 'wait', data: { mode: 'duration', amount: 1, unit: 'days' } }];
    case 'update':
      return [{ type: 'update', data: { action: 'add_tag', value: 'flow-engaged' } }];
    case 'goal':
      return [{ type: 'goal', data: { outcomeTypeId: 'any', withinDays: 14 } }];
    case 'exit':
      return [{ type: 'exit', data: { cooloffDays: 30, note: null } }];
    case 'comment':
      return [{ type: 'comment', data: { text: 'For review: ' } }];
  }
}

export function logicTemplateSpecs(id: LogicTemplateId, idFor: (prefix: string) => string): NodeSpec[] {
  switch (id) {
    case 'wait-check':
      return [...specsFor('wait', idFor), ...specsFor('condition', idFor)];
    case 'ab':
      return [{ type: 'split', data: { rows: [{ id: 'a', label: 'A', pct: 45 }, { id: 'b', label: 'B', pct: 45 }], holdout: { pct: 10 } } }];
    case 'quiet':
      return [{ type: 'wait', data: { mode: 'next', weekday: null, time: '10:00' } }];
  }
}

/** A node built from a spec, at a position. */
export function nodeFromSpec(spec: NodeSpec, id: string, position: { x: number; y: number }): FlowNode {
  return { id, position, ...spec, ...(spec.suggested ? { suggested: spec.suggested } : {}) } as FlowNode;
}

/* -------------------------------------------------------------------------- */
/* Edges                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * After a step's ports change (a template with different buttons, a channel
 * switch, a split row removed), keep the edges that still have a port, move
 * "No reply" onto "After the message" and back where that is the same path,
 * and drop the rest. Never two edges on one port.
 */
export function reconcileEdges(edges: readonly FlowEdge[], node: FlowNode): FlowEdge[] {
  const ports = new Set(portsOf(node).map((port) => port.id));
  const used = new Set<string>();
  const out: FlowEdge[] = [];
  for (const edge of edges) {
    if (edge.from !== node.id) {
      out.push(edge);
      continue;
    }
    let port = edge.port;
    if (!ports.has(port)) {
      if (port === 'noreply' && ports.has('out')) port = 'out';
      else if (port === 'out' && ports.has('noreply')) port = 'noreply';
      else continue;
    }
    if (used.has(port)) continue;
    used.add(port);
    out.push(port === edge.port ? edge : { ...edge, port });
  }
  return out;
}

/**
 * Steps a port may lead to without breaking the graph: not Start, not a
 * comment, not the step itself, and not anything upstream of it (that would
 * be a loop — flows run forward only).
 */
export function connectableTargets(graph: FlowGraph, fromId: string): FlowNode[] {
  return graph.nodes.filter(
    (node) =>
      node.type !== 'trigger' &&
      node.type !== 'comment' &&
      node.id !== fromId &&
      !reachableFrom(graph, node.id).has(fromId),
  );
}

/** Edges a test walk crossed: each step left by its port. */
export function testPathEdges(graph: FlowGraph, steps: readonly FlowTestStep[]): Set<string> {
  const out = new Set<string>();
  for (const step of steps) {
    if (step.nodeId === null || step.port === null) continue;
    const edge = edgeFrom(graph, step.nodeId, step.port);
    if (edge) out.add(edge.id);
  }
  return out;
}

/**
 * The graph in reading order for the list view below 640px: breadth-first
 * from Start along the ports' order, then whatever is not connected, then
 * comments last.
 */
export function readingOrder(graph: FlowGraph): FlowNode[] {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const seen = new Set<string>();
  const out: FlowNode[] = [];
  const start = graph.nodes.find((node) => node.type === 'trigger');
  const queue = start ? [start.id] : [];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (seen.has(id)) continue;
    const node = byId.get(id);
    if (!node) continue;
    seen.add(id);
    out.push(node);
    for (const port of portsOf(node)) {
      const edge = edgeFrom(graph, id, port.id);
      if (edge && !seen.has(edge.to)) queue.push(edge.to);
    }
  }
  for (const node of graph.nodes) if (!seen.has(node.id) && node.type !== 'comment') out.push(node);
  for (const node of graph.nodes) if (!seen.has(node.id) && node.type === 'comment') out.push(node);
  return out;
}

/* -------------------------------------------------------------------------- */
/* Condition rules                                                             */
/* -------------------------------------------------------------------------- */

/** Fields a condition may use: usable in rules and not hidden by the person. */
export function conditionFields(fields: readonly Field[] | null): Field[] {
  return (fields ?? []).filter((field) => field.usableInRules && !field.hidden);
}

/** A starting value for an operator — one the contract accepts wherever a sensible one exists. */
export function defaultLeafValue(op: RuleOperator, type: FieldType, field: Field | undefined): unknown {
  const today = todayIst();
  switch (op) {
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    case 'eq':
      return 0;
    case 'between':
      return type === 'date' ? [today, today] : [0, 10];
    case 'in_last':
    case 'not_in_last':
      return { amount: 30, unit: 'days' };
    case 'before':
    case 'after':
      return today;
    case 'anniversary_in_next':
      return { days: 0 };
    case 'is':
    case 'is_not':
    case 'contains':
      return field?.values?.[0] ?? '';
    case 'any_of':
      return field?.values?.[0] ? [field.values[0]] : [];
    default:
      return undefined;
  }
}

export function leafFor(field: Field, id: string, op?: RuleOperator): RuleLeaf {
  const chosen = op ?? OPERATORS_BY_TYPE[field.type][0] ?? 'known';
  const value = defaultLeafValue(chosen, field.type, field);
  return { kind: 'rule', id, field: field.key, op: chosen, ...(value === undefined ? {} : { value }) } as RuleLeaf;
}

/* -------------------------------------------------------------------------- */
/* Refusals, as sentences about a step                                         */
/* -------------------------------------------------------------------------- */

/**
 * The first thing the contract refuses in a draft, said about the step it is
 * on — "Wait: Write the time as HH:mm" — with the step's id so the editor can
 * open it. Autosave sends nothing the contract would refuse; this is what the
 * top bar says instead of "Saved".
 */
export function draftProblem(error: ZodError, graph: FlowGraph): { message: string; nodeId: string | null } {
  const issue = error.issues[0];
  if (!issue) return { message: 'This draft cannot be saved yet.', nodeId: null };
  const [section, list, index] = issue.path;
  if (section === 'graph' && list === 'nodes' && typeof index === 'number') {
    const node = graph.nodes[index];
    if (node) return { message: `${nodeName(node)}: ${issue.message}`, nodeId: node.id };
  }
  if (section === 'settings') return { message: `Start: ${issue.message}`, nodeId: graph.nodes.find((x) => x.type === 'trigger')?.id ?? null };
  if (section === 'name') return { message: `Name: ${issue.message}`, nodeId: null };
  return { message: issue.message, nodeId: null };
}

export { FLOW_HOLDOUT_PORT, FLOW_JOURNEY_FIELD_LABEL };
